import { useIsMobile } from '@/hooks/useResponsive';
import * as EmailCenterSelectors from '@/selectors/emailCenterSelectors';
import { F, getIntlTranslation } from '@/utils/intl';
import {
  CopyOutlined,
  DeleteOutlined,
  DesktopOutlined,
  DownOutlined,
  EditOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  HolderOutlined,
  LaptopOutlined,
  MobileOutlined,
  RedoOutlined,
  RightOutlined,
  TabletOutlined,
  UndoOutlined,
} from '@ant-design/icons';
import {
  DndContext,
  DragEndEvent,
  DragMoveEvent,
  DragOverlay,
  DragStartEvent,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { Alert, Badge, Button, Segmented, Select, Space, Tooltip } from 'antd';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import { getLocale, useDispatch, useIntl, useSelector } from 'umi';
import { LANGUAGE_CODES, languageLabel } from '../../../constants';
import InputWithPartnerTranslation from '../../InputWithPartnerTranslation';
import SampleSourcePicker from '../SampleSourcePicker';
import TestDataPanel from '../TestDataPanel';
import { humanizeTransKey, humanizeVariablePath } from '../labels';
import { bindingExpression } from '../translationBinding';
import { EmailBodyEditorErrorItem, EmailBodyEditorProps } from '../types';
import { useSampleData } from '../useSampleData';
import CanvasFrame, { DeviceKind } from './CanvasFrame';
import InspectorPanel from './InspectorPanel';
import LayersTree from './LayersTree';
import PaletteRail from './PaletteRail';
import SourcePanel from './SourcePanel';
import { createBlockFromDefinition } from './blockFactory';
import { compile } from './compile';
import { judgeBodyOrigin } from './divergence';
import { Condition, DocumentFidelity } from './document';
import { dragPointFrom, DragSubject, parseDragId } from './dragdrop';
import { loadDocument, stripEnvelope } from './envelope';
import { CanvasMeasurements, DropTarget, Point } from './geometry';
import { issueMessage } from './issueMessages';
import {
  editorActionForKey,
  isTextInputTarget,
  ownsItsOwnFocus,
} from './keymap';
import { resolveBlockDisplayLabel } from './labels';
import { orphanedSlots } from './layers';
import {
  PREFLIGHT,
  preflight,
  preflightErrors,
  preflightWarnings,
} from './preflight';
import { canRedo, canUndo, createEditorState, editorReducer } from './reducer';
import { buildRegistry } from './registry';
import { findBlock } from './tree';
import { conditionalBlockIds, usableClauses } from './visibility';

import styles from './VisualEditorBody.less';

/** Stable default for the `errors` prop — a new [] per render would churn. */
const NO_SERVER_ERRORS: EmailBodyEditorErrorItem[] = [];

/**
 * Simulated device widths, matching the compiler-owned mobile breakpoint.
 *
 * Shown as a **size ramp of device icons** rather than four `NNNpx` labels: the choice
 * being made is "how narrow", and four numbers made the reader parse text to find an
 * ordering the icons state directly. The exact width stays one hover away, because it is
 * occasionally the thing you actually need.
 */
const DEVICE_WIDTHS: {
  width: number;
  icon: React.ReactNode;
  /** Which chrome the canvas draws around the email at this width. */
  device: DeviceKind;
}[] = [
  { width: 360, icon: <MobileOutlined />, device: 'phone' },
  { width: 414, icon: <TabletOutlined />, device: 'phone' },
  { width: 600, icon: <LaptopOutlined />, device: 'tablet' },
  { width: 1024, icon: <DesktopOutlined />, device: 'desktop' },
];

/**
 * Visual editor shell — palette, layers tree, canvas, toolbar.
 *
 * **Write-back is keyed on `state.revision`, not on the compiled body.** The body
 * also changes for reasons the user had nothing to do with — the Redux fragment
 * catalog arriving after mount rebuilds the registry, which can change an emitter —
 * and emitting then would mark a template dirty on open. The revision counter only
 * moves on a dispatched editor action.
 *
 * **Opening a `rawPassthrough` (legacy) document mutates nothing.** The revision
 * guard is what enforces it: write-back fires only on a dispatched edit, so a
 * contained body is emitted verbatim — and the design document goes to the
 * template's `design_document` field beside it, never into the body.
 *
 * The document lives in `editorReducer` state rather than Redux, per
 * docs/redux.md: models fetch, and Redux keeps only the catalogs (fragments,
 * variables, translations, component labels).
 */
const VisualEditorBody: React.FC<EmailBodyEditorProps> = ({
  value = '',
  designJson,
  onDesignJsonChange,
  onDirtyChange,
  previewContext,
  translations,
  fragments,
  fragmentTypes,
  fragmentManifests,
  fragmentNames,
  fragmentOwnership,
  layoutKey,
  layoutOptions,
  onLayoutChange,
  readOnly,
  variableGroups,
  insertableFragments,
  onChange,
  errors: serverErrors = NO_SERVER_ERRORS,
  onBlockingIssuesChange,
  settingsSlot,
  onOpenFragment,
  previewLanguage,
  onPreviewLanguageChange,
  languageOptions,
  onTranslationSave,
  templateName,
  variablesUnsupported,
  defaultView,
  isMarketing,
}) => {
  const intl = useIntl();
  const reduxDispatch = useDispatch();
  /**
   * A phone opens on the narrowest preview, not the 600px one: at 375px the wider
   * canvas is the thing that pushes the whole workspace sideways. The four widths
   * stay switchable — this is only where it starts.
   */
  const isMobile = useIsMobile();
  const [deviceWidth, setDeviceWidth] = useState(() => (isMobile ? 360 : 600));
  /** The chrome that goes with the chosen width — see `DEVICE_WIDTHS`. */
  const deviceKind =
    DEVICE_WIDTHS.find((entry) => entry.width === deviceWidth)?.device ??
    'desktop';
  /** Email texts start folded away; see the panel header below. */
  const [textsCollapsed, setTextsCollapsed] = useState(true);
  /**
   * Which of the left rail's two sections are open.
   *
   * Structure starts folded and is docked to the bottom of the rail: it is the
   * escape hatch for blocks the canvas cannot offer (an empty container, one
   * covered by a child, one hidden by a condition), not the thing you reach for
   * while building. Open, it is capped in CSS and scrolls in its own space, so
   * the blocks above it never get squeezed to a sliver.
   */
  const [railOpen, setRailOpen] = useState({ blocks: true, structure: false });
  const toggleRailSection = useCallback((section: 'blocks' | 'structure') => {
    setRailOpen((current) => ({ ...current, [section]: !current[section] }));
  }, []);
  /**
   * What the centre column shows. A toggle rather than a split: the canvas has to
   * hold a realistic 600 px device width to be worth anything, and halving it to
   * show source permanently would cost more than it gives. G4 adds `preview`.
   *
   * `defaultView` is the caller's entry point, not a preference: **Vorschau** in
   * the template list opens the same drawer as **Bearbeiten**, and the only thing
   * that distinguishes them is landing on Preview rather than Design. Read once,
   * at mount — changing it afterwards would yank the view out from under someone
   * mid-edit.
   */
  /**
   * Phones get the *reading* half of this editor, not the building half.
   *
   * Design and Source are a three-pane arrangement — palette, measured canvas
   * overlay, inspector — that cannot be made to work in 375px, and pointer
   * drag-and-drop onto a measured canvas is not a phone gesture. Measured before
   * this branch existed: the drawer was 375px wide with a 1364px workspace inside
   * it, so the canvas began past the right edge and the inspector sat a thousand
   * pixels off-screen, unreachable and not even scrollable. So the two views that
   * only *read* the email — Preview and Settings — are the two the phone offers,
   * and everything that drives the canvas is left unmounted rather than hidden.
   */
  const [centreView, setCentreView] = useState<
    'design' | 'source' | 'preview' | 'settings'
  >(() => {
    const requested =
      defaultView === 'code' ? 'source' : defaultView ?? 'design';
    // `?view=design` is a real deep link, and on a phone it names a view that is
    // about to be missing from the switcher — so it resolves here, not later.
    if (isMobile && requested !== 'settings') return 'preview';
    return requested;
  });
  /**
   * Preview is not "the canvas plus a data panel" — it is a different mode of the
   * same editor, with every editing affordance out of the way. Read in enough places
   * (toolbar, both rails, keyboard) to be worth naming.
   */
  const previewing = centreView === 'preview';
  /** The email's own properties, as a view rather than a tab in the block rail. */
  const inSettings = centreView === 'settings';
  /**
   * Source is its own two-pane layout — source and a live preview beside it.
   *
   * The palette and the block inspector are meaningless here: there is no selection to
   * inspect and nothing to drag into a text buffer. What you want while editing Twig by
   * hand is to see what it renders, which is the pane that replaces them.
   */
  const inSource = centreView === 'source';

  /**
   * The stored design document travels with the body (they are one logical
   * value in two representations), so the effects that react to a body change
   * read the *latest* prop through a ref rather than listing it as a
   * dependency — a document change is never its own reload trigger.
   */
  const designJsonRef = useRef(designJson);
  designJsonRef.current = designJson;

  const initial = useMemo(
    () => loadDocument(value, layoutKey ?? null, designJson),
    // Mount-time only: subsequent prop changes are handled by the sync effects
    // below, which must not discard edit history.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const [state, dispatch] = useReducer(
    editorReducer,
    initial.document,
    createEditorState,
  );
  const [fidelity, setFidelity] = useState<DocumentFidelity>(initial.fidelity);
  /**
   * The hand-edited source, when the body is detached.
   *
   * Held here rather than in the document: the body is its own field, and the
   * document carries only the `detached` flag plus its snapshot of the blocks —
   * the thing *Restore the visual version* reads back.
   */
  const [rawBody, setRawBody] = useState(() => stripEnvelope(value));
  /** Dismissed the divergence banner without restoring — stays detached. */
  const [detachAcknowledged, setDetachAcknowledged] = useState(false);

  const { document, selectedBlockId } = state;

  /**
   * Manifest labels for the palette, inspector and layers tree.
   *
   * Fetched here rather than by the drawer because this is the only surface that
   * needs them, and the model's guard compares the *resolved locale* — so a
   * repeat mount is a no-op while a language switch refetches.
   */
  const componentLabels = useSelector(
    EmailCenterSelectors.selectComponentLabels,
  );

  /**
   * Which namespaces our own render resolves, as the server declares them.
   * Read from the store rather than taken as a prop: it belongs to the same
   * fetch as `variableGroups`, and every host of this editor would otherwise
   * have to remember to pass the two together — which is how they would end up
   * disagreeing. Undefined until that fetch lands, and the compiler defers
   * nothing until it has. See sendTime.ts.
   */
  const renderTimeNamespaces = useSelector(
    EmailCenterSelectors.selectRenderTimeNamespaces,
  );
  useEffect(() => {
    reduxDispatch({
      type: 'emailCenter/fetchComponentLabels',
      payload: { locale: getLocale() },
    });
  }, [reduxDispatch]);

  /**
   * Re-load when the *stored* body changes underneath us — a template switch, or
   * a code-mode edit. Keyed on `value` alone and guarded by the last value we
   * loaded, because re-loading is destructive: it replaces the document and
   * clears undo history.
   */
  const loadedValueRef = useRef(value);
  useEffect(() => {
    if (value === loadedValueRef.current) return;
    loadedValueRef.current = value;
    const loaded = loadDocument(
      value,
      layoutKey ?? null,
      designJsonRef.current,
    );
    setFidelity(loaded.fidelity);
    setRawBody(stripEnvelope(value));
    setDetachAcknowledged(false);
    dispatch({
      type: 'replaceDocument',
      document: loaded.document,
      resetHistory: true,
    });
    // `layoutKey` is read but deliberately not a dependency: a layout change is
    // its own action (below), not a reason to reload the body.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  /**
   * **A contained body follows the template's layout; an authored one does not.**
   *
   * The distinction is the whole reason the blanket sync had to go (see below), and
   * missing it cost preview fidelity: `appointment_confirm XXXL` stores
   * `layout_key = layout/xxxl/base`, but its document carried `null` — the prop is
   * undefined on the first render, and `initial` is captured once — so the canvas
   * rendered the email **without its layout** while the send has one.
   *
   * Why it is safe here and not in general: a `rawPassthrough` document is emitted
   * **verbatim**, so no `{% block %}` wrapper is fabricated and there is no wrapper
   * name to mismatch. The empty-email bug came from wrapping *authored* content in
   * a block the layout never declares — which cannot happen for a contained body.
   */
  const syncedLayoutRef = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    const next = layoutKey ?? null;
    if (!document.rawPassthrough) return;
    if (document.layoutKey === next || syncedLayoutRef.current === next) return;
    syncedLayoutRef.current = next;

    /**
     * **A body can load contained purely because its layout was not known yet.**
     *
     * `TemplateDrawer` fills the prop from the fetched template, so it is undefined
     * on the first render — and `initial` is captured once. `loadDocument` needs the
     * layout to split a body into its `{% block %}` overrides, so without this the
     * templates this is for would still open locked. Re-opening is destructive, so
     * it happens only while the document is untouched and only when it changes the
     * answer.
     */
    if (next && state.revision === 0) {
      const reloaded = loadDocument(value, next, designJsonRef.current);
      if (!reloaded.document.rawPassthrough) {
        setFidelity(reloaded.fidelity);
        dispatch({
          type: 'replaceDocument',
          document: reloaded.document,
          resetHistory: true,
        });
        return;
      }
    }

    // `silent`: syncing the template's own layout is not a user edit, and counting
    // it as one would write an envelope into a legacy template merely by opening it.
    dispatch({ type: 'setLayout', layoutKey: next, silent: true });
    // `value` and `revision` are read, not watched: a body or edit-count change is
    // not itself a reason to re-sync the layout.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layoutKey, document.rawPassthrough, document.layoutKey]);

  /**
   * **For an authored document the `layoutKey` prop is deliberately *not* synced.**
   *
   * Verified in the browser: doing so empties the email. `TemplateDrawer` computes
   * the prop as `template.layout_key || partnerDefaultLayout`, so for a template
   * with no layout of its own it carries the partner *fallback* — here
   * `layout/xxxl/base`. Pushing that into the document made `compile()` wrap the
   * blocks in `{% block content %}`, a block that layout never declares, and Twig
   * discards content outside a declared block. The canvas rendered 7,371
   * characters of bare chrome with every block gone: no error, no warning.
   *
   * The document's own `layoutKey` is the authority (see `renderLayoutKey` below),
   * and choosing a layout from inside the editor needs slot mapping to go with it
   * — Sprint 6.5. `preflight` now flags a document whose layout declares no
   * matching block, so the failure is a blocked save rather than an empty send.
   */

  // Derived from the fragment catalog rather than hard-coded: a component
  // shipped as a Twig file becomes buildable with no change here.
  const registry = useMemo(
    () =>
      buildRegistry(fragments, fragmentTypes, fragmentManifests, fragmentNames),
    [fragments, fragmentTypes, fragmentManifests, fragmentNames],
  );

  /**
   * **The document's layout is the authority, not the `layoutKey` prop.**
   *
   * `compile()` decides whether to emit a `{% block %}` wrapper from
   * `document.layoutKey`, and the renderer decides whether to prepend
   * `{% extends %}` from whatever it is given. If those two disagree the email
   * is silently destroyed: bare content wrapped in an extends lands outside every
   * block and Twig discards it, leaving nothing but layout chrome.
   *
   * They can disagree easily. `TemplateDrawer` computes its `layoutKey` prop as
   * `template.layout_key || partnerDefaultLayout`, while an envelope-decoded
   * document carries the layout the editor actually authored against — which may
   * be null. Deriving both from the document makes them agree by construction.
   */
  const renderLayoutKey = document.layoutKey;

  const compiled = useMemo(
    () => compile(document, { registry, variableGroups, renderTimeNamespaces }),
    [document, registry, variableGroups, renderTimeNamespaces],
  );

  /**
   * **When detached, the body is the authority — not the document.**
   *
   * Everything downstream (the canvas, preflight, write-back) takes this, so a
   * hand-edited template previews and saves exactly what the user wrote, while the
   * document rides along in the envelope as the snapshot *Restore the visual
   * version* needs.
   */
  const detached = document.detached === true;
  // When detached the raw body IS the output — the document (with its
  // `detached` flag and block snapshot) rides beside it in design_document,
  // which is what *Restore the visual version* reads back.
  const body = detached ? rawBody : compiled.body;
  const warnings = detached ? [] : compiled.warnings;

  /**
   * What the **design** canvas renders: the same body with the `{% if %}` wrappers
   * left off, so every block a recipient could see is visible while authoring.
   *
   * Design shows *everything the email can contain*, marked as conditional; Preview
   * shows what one recipient gets, where a false condition hiding a block is exactly
   * right. A detached body is carried verbatim, so there is nothing to re-emit.
   */
  const designBody = useMemo(
    () =>
      detached
        ? body
        : compile(document, {
            registry,
            variableGroups,
            renderTimeNamespaces,
            ignoreVisibility: true,
          }).body,
    [detached, body, document, registry, variableGroups, renderTimeNamespaces],
  );

  // The save gate. Local-only today, so it reports rather than blocks — but a
  // missing fragment key is worth surfacing even in preview: it means this
  // template would fail to render for real recipients.
  const checks = useMemo(
    () =>
      preflight(document, body, warnings, {
        fragments,
        fragmentTypes,
        registry,
      }),
    [document, body, warnings, fragments, fragmentTypes, registry],
  );

  /**
   * A body edited outside this editor — in code mode, or straight through the API.
   *
   * Checked once per loaded value, against the *compiled* form of the decoded
   * document. Only for `exact` documents: a legacy body is described exactly by its
   * one raw block, and an already-detached one needs no detecting.
   *
   * `undecidable` deliberately does **not** record the value as checked: it means the
   * fragment catalog has not arrived yet, so the question is asked again when it
   * does. Recording it would leave the answer at whatever the first render could see.
   */
  const divergenceCheckedRef = useRef<string | null>(null);
  useEffect(() => {
    if (fidelity !== 'exact' || detached) return;
    if (divergenceCheckedRef.current === value) return;

    const origin = judgeBodyOrigin(value, compiled);
    if (origin === 'undecidable') return;
    divergenceCheckedRef.current = value;

    if (origin === 'hand-edited') {
      setRawBody(stripEnvelope(value));
      dispatch({ type: 'setDetached', detached: true });
    }
  }, [value, fidelity, detached, compiled]);

  /**
   * Preflight while the fragment catalog is still loading reports every reference as
   * unresolvable — "6 problems would stop this email from rendering" on a template with
   * nothing wrong with it. Same rule as the canvas and as `judgeBodyOrigin`: an answer
   * that depends on data that has not arrived is not an answer.
   */
  const catalogPending = !fragments || Object.keys(fragments).length === 0;
  const errors = useMemo(
    () => (catalogPending ? [] : preflightErrors(checks)),
    [catalogPending, checks],
  );
  const advisories = useMemo(
    () => (catalogPending ? [] : preflightWarnings(checks)),
    [catalogPending, checks],
  );

  /**
   * A layout problem already has a notice bar, so the canvas does not restate it as an
   * outcome underneath the frame — see `CanvasFrame`'s `layoutIssueReported`. These three
   * are the same failure named at its cause: content the layout discards.
   */
  const layoutIssueReported = useMemo(
    () =>
      errors.some((issue) =>
        (
          [
            PREFLIGHT.MISSING_LAYOUT,
            PREFLIGHT.LAYOUT_NOT_A_LAYOUT,
            PREFLIGHT.LAYOUT_SLOT_NOT_DECLARED,
          ] as string[]
        ).includes(issue.code),
      ),
    [errors],
  );

  /**
   * Write-back: the compiled body into the form field the drawer saves.
   *
   * Fires only when `revision` moves, i.e. from a dispatched action. The emitted
   * value is recorded as "already loaded" so the round-trip back through `value`
   * does not look like an external change and reload the document (which would
   * clear undo history mid-edit).
   *
   * A body with blocking issues is still emitted: not emitting would leave the form
   * holding an older body while the editor shows something else, so a save would
   * silently persist the wrong thing. The *save* is what gets blocked, via
   * `onBlockingIssuesChange` — see the validator in `TemplateDrawer`.
   */
  const emittedRevisionRef = useRef(state.revision);
  useEffect(() => {
    if (state.revision === emittedRevisionRef.current) return;
    emittedRevisionRef.current = state.revision;

    /**
     * A contained (legacy) body now writes back too — the revision guard is what
     * keeps opening a template from touching it, and that guard is enough. The
     * first *edit* is the user's change and saves like any other.
     */
    if (!onChange || readOnly) return;

    loadedValueRef.current = body;
    onChange(body);
    /**
     * The document goes up with the body — they are saved together, the body
     * as what renders and the document as design_document beside it.
     *
     * A contained (`rawPassthrough`) document stays null: it is *derived from*
     * the body on every open, not authored state, and storing it would freeze
     * a snapshot that a later API-side body edit silently invalidates.
     */
    onDesignJsonChange?.(document.rawPassthrough ? null : document);
  }, [
    state.revision,
    body,
    document,
    onChange,
    onDesignJsonChange,
    readOnly,
    document.rawPassthrough,
  ]);

  /**
   * Sample data for the preview — the same hook the code editor's preview uses, so
   * the two cannot drift. It needs the *output* body, so it sits after `body`.
   */
  /**
   * The language everything language-shaped in the editor uses: the preview's
   * translations, the sample-data overrides, and which locale a translated field
   * shows. One value, because two would let the preview and the field it came from
   * disagree about what they are showing.
   *
   * Falls back to a real code rather than a placeholder — it also names the locale a
   * translation is *saved* to, and saving to `default` would file wording under a
   * language nobody reads.
   */
  const activeLanguage =
    previewLanguage ?? languageOptions?.[0] ?? LANGUAGE_CODES[0];

  const sample = useSampleData({
    body,
    previewContext,
    translations,
    fragments,
    layoutKey: renderLayoutKey,
  });

  /**
   * Does the preview's right rail have anything to put in it?
   *
   * For an NL2Go template the variable tooling is withheld (see
   * `variablesUnsupported`), which usually leaves the rail with nothing but its
   * own border — and an empty 340 px column is width the rendered email should
   * have had.
   */
  const sampleRailShown = !variablesUnsupported || sample.transKeys.length > 0;

  /**
   * Blocks that only sometimes appear, for the canvas to mark.
   *
   * Without a marker you are editing an email that renders differently for different
   * recipients with no way to tell which parts those are — the condition would live only
   * in a panel you have to select the block to see.
   */
  const conditionalBlocks = useMemo(
    () => conditionalBlockIds(document.blocks),
    [document],
  );

  /** Lets the host refuse to save a body that would not render. */
  useEffect(() => {
    onBlockingIssuesChange?.(errors.length);
  }, [errors.length, onBlockingIssuesChange]);

  /**
   * A legacy body is carried verbatim (`rawPassthrough`), so a block inserted
   * beside it would be appended *after* a complete HTML document. Rebuilding such
   * a template is an explicit, undoable choice — Sprint 7.2.
   */
  /**
   * Blocks cannot be *added* to a contained body: it carries its own `{% block %}`
   * overrides, so anything placed beside them lands outside every block and Twig
   * discards it. The body itself is editable — as the raw block (Block tab) or in
   * the Source view — which is what "everything is possible" rests on here.
   */
  const structureLocked = document.rawPassthrough === true;

  /**
   * Two different prohibitions, kept apart because conflating them broke the thing
   * G3 exists for:
   *
   * - **Arranging blocks** — palette, drag and drop, delete/duplicate keys — is off
   *   for a contained body (nothing can be placed around its own `{% block %}`
   *   overrides) and while detached (the document is a stale snapshot of the output).
   * - **Editing a block's properties** is off only when the template is read-only or
   *   detached. A contained body's raw block is precisely what must stay editable —
   *   one flag for both left its code field `readOnly`, which also made it
   *   unfocusable, so the keystrokes went to the page and triggered a navigation
   *   shortcut instead.
   */
  const arrangingDisabled = readOnly === true || structureLocked || detached;

  /**
   * Never sit on Design for a structure-locked body.
   *
   * `centreView` starts as 'design', and the option is removed above for these
   * templates — leaving the state there would show a canvas that cannot be
   * edited under a switcher with nothing selected. Source is where the content
   * actually is. Runs on the flag rather than on mount because `rawPassthrough`
   * is only known once the document has loaded.
   */
  const autoSourceRef = useRef(false);
  useEffect(() => {
    /**
     * Rotating a phone into landscape crosses the breakpoint, so this runs again
     * on a document someone is part-way through reading. It only ever moves *to*
     * Preview — nothing here puts anyone into a canvas they did not ask for.
     */
    if (isMobile) {
      if (centreView === 'design' || centreView === 'source') {
        setCentreView('preview');
      }
      return;
    }
    if (structureLocked) {
      if (centreView === 'design') {
        autoSourceRef.current = true;
        setCentreView('source');
      }
      return;
    }
    /**
     * The lock can lift a tick after it went on: a body containing nothing the
     * canvas can hold splits into its `{% block %}` regions once the template's
     * layout arrives (the sync effect above), and `layoutKey` reaches this editor
     * after the body does. Leaving the view where the lock put it would open such
     * a template on Source for good, which is the state this effect exists to
     * avoid. Only our own switch is undone, and only while nothing has been
     * edited — a typed-in source buffer is never yanked out from under anyone.
     */
    if (
      autoSourceRef.current &&
      centreView === 'source' &&
      state.revision === 0
    ) {
      autoSourceRef.current = false;
      setCentreView('design');
    }
  }, [isMobile, structureLocked, centreView, state.revision]);
  const propertiesDisabled = readOnly === true || detached;

  /**
   * The toolbar's layout switch. The host persists the key (it is the same
   * `layout_key` field the Settings select writes), and the view follows
   * immediately: a contained body picks the new key up through the silent
   * prop-sync above; an authored document owns its layout, so for it the
   * switch is dispatched as its own, undoable edit.
   *
   * Clearing it is a first-class pick, not a non-answer: an email with no layout
   * is what the server renders when `layout_key` is empty (it sends the body as
   * it stands), and until the clear existed the choice was one-way — the first
   * layout picked could only ever be swapped for another one. `undefined` is what
   * antd's clear button reports; it is normalised to `null` so the host writes an
   * explicit "no layout" rather than dropping the field from its payload.
   */
  const handleLayoutSwitch = useCallback(
    (key?: string | null) => {
      const next = key ?? null;
      onLayoutChange?.(next);
      if (!document.rawPassthrough) {
        dispatch({ type: 'setLayout', layoutKey: next });
      }
    },
    [onLayoutChange, document.rawPassthrough],
  );

  /**
   * Which slot of the selected container the next click-insert should fill.
   *
   * Local rather than reducer state: it is a pointer into the *view*, not part of
   * the document, and it exists because a multi-column layout was otherwise
   * unusable without dragging — every click-insert landed in column 1
   * (`insertionLocationFor`).
   */
  const [preferredSlot, setPreferredSlot] = useState<number | null>(null);

  const handleSelectBlock = useCallback((blockId: string | null) => {
    setPreferredSlot(null);
    dispatch({ type: 'selectBlock', blockId });
  }, []);

  /** A slot node in the structure tree: select its container, remember the slot. */
  const handleSelectSlot = useCallback((parentId: string, slot: number) => {
    setPreferredSlot(slot);
    dispatch({ type: 'selectBlock', blockId: parentId });
  }, []);

  /**
   * Tell the host as soon as there is something to lose.
   *
   * `canUndo` is the whole condition: the reducer pushes history only for a real
   * mutation, and the layout sync at mount is applied with `fromTemplate` so it
   * never lands there. Reported up rather than kept here because the drawer owns
   * closing, and it is the close that discards the work.
   */
  useEffect(() => {
    onDirtyChange?.(canUndo(state));
  }, [state, onDirtyChange]);

  /**
   * A new block of `type`, with required props filled.
   *
   * The starter copy is translated here, where an intl instance exists, rather
   * than inside the pure factory; without it a fresh button fails preflight on a
   * missing required prop the moment it is inserted.
   */
  const buildBlock = useCallback(
    (type: string) => {
      const definition = registry.definitions[type];
      if (!definition) return null;

      const starter = getIntlTranslation(intl, {
        id: 'pages.emailCenter.visual.starter.text',
        defaultMessage: 'Your text here',
      });

      return createBlockFromDefinition(definition, {
        placeholders: { richtext: starter, text: starter, url: '#' },
      });
    },
    [registry, intl],
  );

  /**
   * Deliberately does **not** close over the document: the reducer resolves the
   * location from its own state (`insertBlock`), which keeps this callback stable
   * while typing and so keeps every palette entry — each a dnd-kit draggable — out
   * of the per-keystroke render.
   */
  const handleInsert = useCallback(
    (type: string) => {
      const block = buildBlock(type);
      if (block) dispatch({ type: 'insertBlock', block, preferredSlot });
    },
    [buildBlock, preferredSlot],
  );

  /**
   * The inspector's write path. `coalesce` is the control's call, not this
   * component's: a run of keystrokes in a text field is one undo step, while
   * picking a colour or flipping a toggle is its own.
   */
  /**
   * Visibility, stored **as edited**.
   *
   * Deliberately *not* normalised here. Normalising on every change deletes a clause the
   * instant it is created — a fresh clause has no variable yet, `normalizeCondition`
   * calls that unusable, and the condition vanishes before it can be filled in. Found in
   * the browser: "add a condition" produced nothing at all.
   *
   * Normalisation belongs at the boundary where a half-typed condition would *escape*,
   * which is the compiler: `compile()` already skips clauses with no path and warns
   * (`EMPTY_CONDITION`) when none is usable, so a half-written condition never reaches a
   * recipient. The document is allowed to hold work in progress; the body is not.
   */
  const handleChangeVisibility = useCallback(
    (condition: Condition | null) => {
      if (!selectedBlockId) return;
      dispatch({
        type: 'setVisibility',
        blockId: selectedBlockId,
        visibility: condition,
      });
    },
    [selectedBlockId],
  );

  const handleChangeProps = useCallback(
    (props: Record<string, unknown>, options?: { coalesce?: string }) => {
      if (!selectedBlockId) return;
      dispatch({
        type: 'setProps',
        blockId: selectedBlockId,
        props,
        // The run key is scoped here, from the same id the action carries, so the
        // write and its history entry can never name different blocks.
        coalesce: options?.coalesce
          ? `${selectedBlockId}:${options.coalesce}`
          : undefined,
      });
    },
    [selectedBlockId],
  );

  const handleEndEditRun = useCallback(() => {
    dispatch({ type: 'endEditRun' });
  }, []);

  /**
   * Editing the Twig source. The first keystroke detaches: from then on the body is
   * the authority and block editing is off, because the document no longer describes
   * what is stored.
   */
  const handleSourceChange = useCallback(
    (next: string) => {
      setRawBody(next);
      if (!detached) dispatch({ type: 'setDetached', detached: true });
      else dispatch({ type: 'touch' });
    },
    [detached],
  );

  /** Drops the code edits and goes back to the visual version. */
  const handleRestoreVisual = useCallback(() => {
    setRawBody(stripEnvelope(compiled.body));
    setDetachAcknowledged(false);
    dispatch({ type: 'setDetached', detached: false });
  }, [compiled.body]);

  /**
   * Drag-and-drop.
   *
   * dnd-kit supplies the gesture — sensors, activation constraints, the drag
   * lifecycle — while *where the block lands* comes from `dropTargetAtPoint`
   * against the canvas iframe's measured rects, which dnd-kit cannot see. The two
   * meet at the draggable id (`dragdrop.ts`) and at the pointer position.
   *
   * The live target is kept in a **ref**, not state: `CanvasFrame` already paints
   * the indicator from its own copy, this side only needs the value at drop time,
   * and storing it in state would re-render the shell on every pointer move —
   * which re-renders the canvas, which re-measures.
   */
  const [dragSubject, setDragSubject] = useState<DragSubject | null>(null);
  const [dragPoint, setDragPoint] = useState<Point | null>(null);
  const dropTargetRef = useRef<DropTarget | null>(null);

  const mouseSensor = useSensor(MouseSensor, {
    // A distance threshold, so a palette *click* is not read as a tiny drag —
    // click-to-insert is the keyboard-reachable path and must not regress.
    activationConstraint: { distance: 6 },
  });
  const touchSensor = useSensor(TouchSensor, {
    activationConstraint: { delay: 200, tolerance: 8 },
  });
  /**
   * No sensors on a phone: nothing draggable is mounted there (the palette and the
   * canvas grip are both design-only), so these would listen for a gesture that can
   * never start. The provider itself stays — `useSensors` must keep its hook order.
   */
  const sensors = useSensors(...(isMobile ? [] : [mouseSensor, touchSensor]));

  const handleDragStart = useCallback((event: DragStartEvent) => {
    const subject = parseDragId(event.active.id);
    if (!subject) return;
    setDragSubject(subject);
    setDragPoint(
      dragPointFrom(event.activatorEvent as MouseEvent, { x: 0, y: 0 }),
    );
  }, []);

  const handleDragMove = useCallback(
    (event: DragMoveEvent) => {
      if (!dragSubject) return;
      setDragPoint(
        dragPointFrom(event.activatorEvent as MouseEvent, event.delta),
      );
    },
    [dragSubject],
  );

  const handleDropTargetChange = useCallback((target: DropTarget | null) => {
    dropTargetRef.current = target;
  }, []);

  /**
   * The slots the canvas actually rendered.
   *
   * A component may declare more slots than it renders, conditionally on its own
   * props — `core/columns@1` only renders its third column when
   * `layout == 'three'` — and content compiled into a block that does not exist is
   * discarded by Twig without a word. The rendered markers are the only generic
   * way to know, and the canvas measures them anyway.
   *
   * Stored as a set of keys rather than the whole measurement, and only replaced
   * when the key set really changes: this is fed by `onMeasure`, so updating on
   * every pass would re-render the canvas, which re-measures.
   */
  const [renderedSlots, setRenderedSlots] = useState<ReadonlySet<string>>();
  const handleMeasure = useCallback((measurements: CanvasMeasurements) => {
    const keys = (measurements.slots ?? []).map(
      (entry) => `${entry.parentId}:${entry.slot}`,
    );
    setRenderedSlots((current) => {
      if (
        current &&
        current.size === keys.length &&
        keys.every((key) => current.has(key))
      ) {
        return current;
      }
      return new Set(keys);
    });
  }, []);

  /**
   * The canvas measures which slots actually rendered, and it only exists in the
   * Design view — so switching away unmounts it and the set it reported goes
   * stale.
   *
   * Clearing on every view change restores `orphanedSlots`' "don't know yet"
   * contract (it returns nothing for `undefined`). Without this, returning to
   * Design judged a freshly-mounted canvas against the *previous* measurement
   * for the frame or two before it re-measured, so every slot briefly looked
   * unrendered: a "N blocks sit in parts the current settings do not show"
   * banner flashed in and out and shoved the editor down ~100px each time.
   */
  useEffect(() => {
    setRenderedSlots(undefined);
  }, [centreView]);

  /** Blocks stranded in a slot the component does not render. */
  const measuredStranded = useMemo(
    () => orphanedSlots(document.blocks, renderedSlots),
    [document, renderedSlots],
  );

  /**
   * The stranded warning, held back until the measurement settles.
   *
   * A remounting canvas reports its slots incrementally: the first measurement
   * after switching back to Design lands before every slot has painted, so
   * `orphanedSlots` momentarily sees most of them as missing. Rendering that
   * directly flashed a "N blocks sit in parts the current settings do not show"
   * banner for ~30ms on *every* Design/Source toggle, shoving the editor down
   * ~100px and back — measured, not theorised.
   *
   * Clearing `renderedSlots` on a view change (above) is not enough on its own,
   * because the transient is the *incoming* partial measurement, not the stale
   * outgoing one.
   *
   * A short settle window is the right shape here because this warning is
   * advisory and about a steady state — "this content would be left out" is
   * never urgent enough to be worth showing before it is known to be true.
   * Clearing is immediate: once the content is no longer stranded, say so at once.
   */
  const [stranded, setStranded] = useState<ReturnType<typeof orphanedSlots>>(
    [],
  );

  useEffect(() => {
    if (measuredStranded.length === 0) {
      setStranded([]);

      return;
    }
    const timer = setTimeout(() => setStranded(measuredStranded), 300);

    return () => clearTimeout(timer);
  }, [measuredStranded]);

  /**
   * Drops a slot aim that no longer exists — changing a columns block from three
   * back to two would otherwise leave the next insert pointed at a column that is
   * not rendered.
   */
  useEffect(() => {
    if (preferredSlot == null || !selectedBlockId || !renderedSlots) return;
    if (!renderedSlots.has(`${selectedBlockId}:${preferredSlot}`)) {
      setPreferredSlot(null);
    }
  }, [preferredSlot, selectedBlockId, renderedSlots]);

  const endDrag = useCallback(() => {
    setDragSubject(null);
    setDragPoint(null);
    dropTargetRef.current = null;
  }, []);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const subject = parseDragId(event.active.id) ?? dragSubject;
      const target = dropTargetRef.current;
      endDrag();

      // No target means the pointer was released off-canvas. Dropping nothing is
      // the correct outcome — `dropTargetAtPoint` would happily answer for a point
      // over the inspector, and a stray release must not append to the email.
      if (!subject || !target || arrangingDisabled) return;

      if (subject.kind === 'palette') {
        const block = buildBlock(subject.type);
        if (block) {
          dispatch({ type: 'addBlock', block, location: target.location });
        }
        return;
      }

      dispatch({
        type: 'moveBlock',
        blockId: subject.blockId,
        location: target.location,
      });
    },
    [dragSubject, endDrag, arrangingDisabled, buildBlock],
  );

  const selectedBlock = useMemo(
    () =>
      selectedBlockId ? findBlock(document.blocks, selectedBlockId) : null,
    [document, selectedBlockId],
  );

  /**
   * Whether the rail's eye has the condition editor open, per block.
   *
   * Held as `{blockId, open}` rather than a bare boolean so selecting another block
   * falls back to the default below instead of carrying one block's disclosure state
   * onto the next.
   */
  const [conditionsToggle, setConditionsToggle] = useState<{
    blockId: string;
    open: boolean;
  } | null>(null);

  /** How many clauses would actually compile — what the eye's marker counts. */
  const conditionCount = selectedBlock?.visibility
    ? usableClauses(selectedBlock.visibility).length
    : 0;

  // A block that has conditions opens showing them; one that has none opens closed,
  // which is the state the eye is there to change.
  const conditionsOpen =
    conditionsToggle && conditionsToggle.blockId === selectedBlockId
      ? conditionsToggle.open
      : conditionCount > 0;

  /**
   * Claims focus when the user starts interacting, so the shortcuts below reach
   * `handleKeyDown`.
   *
   * Verified in the browser: clicking a block on the canvas leaves
   * `document.activeElement` on the Ant Design drawer root — *outside* this
   * container — so a following ⌘Z never fires the handler at all. Nothing about
   * the interaction looks broken; undo just silently does nothing.
   *
   * Only claimed when focus is not already inside, so a field in the editor (the
   * inspector's inputs, from 2.3) keeps it. `preventScroll` because the drawer is
   * a long scrolled form and focusing must not jump it.
   */
  const shellRef = useRef<HTMLDivElement | null>(null);
  const engagedRef = useRef(false);
  const handleMouseDown = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      engagedRef.current = true;
      const shell = shellRef.current;
      if (!shell || shell.contains(window.document.activeElement)) return;
      // Never take focus from a control that the browser is about to focus itself:
      // doing so left focus on the shell and sent the keystrokes to the page.
      if (ownsItsOwnFocus(event.target)) return;
      shell.focus({ preventScroll: true });
    },
    [],
  );

  /**
   * Keeps the shortcuts alive across mutations that remove whatever held focus —
   * deleting the selected block, or undoing an insert while a tree node inside it
   * was focused. Both drop `activeElement` to `<body>`, after which no keystroke
   * reaches the editor.
   *
   * Narrow on purpose: it only acts once the user has engaged with the editor, and
   * only when focus went nowhere at all. Focus that moved to another control (a
   * form field elsewhere in the drawer) is left alone.
   */
  useEffect(() => {
    const shell = shellRef.current;
    if (!engagedRef.current || !shell) return;
    if (window.document.activeElement !== window.document.body) return;
    shell.focus({ preventScroll: true });
  }, [document]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      const action = editorActionForKey(
        {
          key: event.key,
          metaKey: event.metaKey,
          ctrlKey: event.ctrlKey,
          shiftKey: event.shiftKey,
          altKey: event.altKey,
          fromTextInput: isTextInputTarget(event.target),
        },
        // Preview edits nothing, so it takes no editing keystrokes either —
        // ⌘Z reordering an email you are only *looking at* would be a surprise.
        { selectedBlockId, readOnly: arrangingDisabled || previewing },
      );
      if (!action) return;

      event.preventDefault();
      dispatch(action);
    },
    [selectedBlockId, arrangingDisabled, previewing],
  );

  /**
   * Where the block context menu is open, in viewport coordinates.
   *
   * Rendered here rather than in the canvas because its actions are reducer
   * actions — the canvas has the geometry, this has the dispatcher.
   */
  const [blockMenu, setBlockMenu] = useState<{
    blockId: string;
    x: number;
    y: number;
  } | null>(null);

  const openBlockMenu = useCallback(
    (blockId: string, at: { x: number; y: number }) =>
      setBlockMenu({ blockId, x: at.x, y: at.y }),
    [],
  );

  const closeBlockMenu = useCallback(() => setBlockMenu(null), []);

  // Any click, scroll or Escape dismisses it — a menu pinned to a block's old
  // position after the canvas moves under it is worse than no menu.
  useEffect(() => {
    if (!blockMenu) return undefined;
    const dismiss = () => closeBlockMenu();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeBlockMenu();
    };
    window.addEventListener('click', dismiss);
    window.addEventListener('scroll', dismiss, true);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('click', dismiss);
      window.removeEventListener('scroll', dismiss, true);
      window.removeEventListener('keydown', onKey);
    };
  }, [blockMenu, closeBlockMenu]);

  /**
   * The fragment behind the selection, when it is one this partner may edit.
   *
   * `core/*` is the editor's own component library — one global copy serves every
   * partner — so offering "edit this building block" on one invites a change whose
   * blast radius is far wider than the email in front of you.
   *
   * A standard block the partner does not own is withheld for the opposite
   * reason: its row cannot be written at all, so the action could only produce a
   * copy under a new key, leaving this email on the original and the canvas
   * unchanged. Copying stays in the Theme tab, which lists the copy beside the
   * block it came from ({@see isFragmentOpenableFromTemplate}).
   *
   * The `core/*` test is the key prefix, not `inferred`. `inferred` means
   * "declares no manifest", which is a different question:
   * `general/partials/action_buttons` declares one and is still an ordinary
   * editable partial — gating on `inferred` hid the icon on exactly the blocks it
   * was asked for. Same rule the Source view's fragment picker already uses.
   */
  const selectedFragmentKey = useMemo(() => {
    if (!selectedBlock || !onOpenFragment) return null;
    const key = registry.definitions[selectedBlock.type]?.fragmentKey;
    if (!key || key.startsWith('core/')) return null;
    return fragmentOwnership?.[key] === true ? key : null;
  }, [selectedBlock, registry, onOpenFragment, fragmentOwnership]);

  // Names the selection the way the palette does: the component's declared
  // label, falling back to a humanized component name.
  const selectedLabel = useMemo(() => {
    if (!selectedBlock) return null;
    const definition = registry.definitions[selectedBlock.type];
    return definition
      ? resolveBlockDisplayLabel(definition, componentLabels)
      : selectedBlock.type;
  }, [selectedBlock, registry, componentLabels]);

  /**
   * What to write on the thing under the cursor while dragging.
   *
   * Named from the registry, like every other surface — the palette, the structure
   * tree and the selection readout all go through `resolveBlockDisplayLabel`, and a
   * drag that called the same block something else would be its own bug.
   */
  const dragLabel = useMemo(() => {
    if (!dragSubject) return null;
    const type =
      dragSubject.kind === 'palette'
        ? dragSubject.type
        : findBlock(document.blocks, dragSubject.blockId)?.type;
    if (!type) return null;
    const definition = registry.definitions[type];
    return definition
      ? resolveBlockDisplayLabel(definition, componentLabels)
      : type;
  }, [dragSubject, document, registry, componentLabels]);

  /**
   * Each issue as a translated sentence with the technical detail underneath.
   *
   * `detail` names components, params and fragment keys and is deliberately
   * English — it is what makes a support ticket actionable — so it stays, but as
   * secondary text rather than as the message a partner has to interpret.
   */
  type Issue = {
    code: string;
    blockId?: string;
    fragmentKey?: string;
    detail: string;
  };

  /**
   * Take the author to the block an issue is about.
   *
   * Every issue already carries `blockId`; nothing used it, so "A block is missing
   * something it needs" was the whole answer — in a twenty-block email, a hunt. The
   * canvas is where a block can be seen and fixed, so the jump also leaves whatever
   * view is open.
   */
  const focusIssueBlock = useCallback(
    (blockId: string) => {
      setCentreView('design');
      dispatch({ type: 'selectBlock', blockId });
    },
    [dispatch],
  );

  const issueText = (issue: Issue) => {
    const message = issueMessage(issue.code);
    const body = message ? (
      <F id={message.id} defaultMessage={message.defaultMessage} />
    ) : (
      issue.detail
    );
    // The identifier-bearing technical text is support material, not reading
    // material — on the title so it stays reachable without costing a line.
    return issue.blockId ? (
      <button
        type="button"
        className={styles.issueLink}
        title={issue.detail}
        onClick={() => focusIssueBlock(issue.blockId as string)}
      >
        {body}
      </button>
    ) : (
      <span title={issue.detail}>{body}</span>
    );
  };

  const issueList = (items: Issue[]) => (
    <ul className={styles.issueList}>
      {items.map((issue) => (
        <li
          key={`${issue.code}-${issue.blockId ?? issue.fragmentKey}`}
          // The identifier-bearing technical text is support material, not
          // reading material — on the title so it stays reachable without
          // costing a line per issue.
          title={issue.detail}
        >
          {issueText(issue)}
        </li>
      ))}
    </ul>
  );

  /**
   * A preflight notice, sized to what it has to say.
   *
   * The full treatment — a counted summary line, then the issues beneath it, then
   * a technical detail line under each — spent ~150px of the editor restating one
   * sentence three ways. A single issue is now its own one-line message with no
   * description; only a genuine list keeps the summary and expands.
   */
  const noticeAlert = (
    type: 'warning' | 'error',
    items: Issue[],
    summary: React.ReactNode,
  ) =>
    items.length === 0 ? null : (
      <Alert
        className={styles.noticeBar}
        type={type}
        showIcon
        message={items.length === 1 ? issueText(items[0]) : summary}
        description={items.length === 1 ? undefined : issueList(items)}
      />
    );

  /**
   * One line of what the server refused to save.
   *
   * Twig is compiled where the template is stored, so these exist only after a
   * failed save and no local check can predict them. They keep the API's own
   * wording — a line number and a compiler message are the whole value of the
   * report, and paraphrasing either would cost the only thing that locates the
   * mistake.
   */
  const serverErrorText = (item: EmailBodyEditorErrorItem) =>
    item.line == null ? (
      <>{item.message}</>
    ) : (
      <F
        id="pages.emailCenter.visual.serverErrorLine"
        defaultMessage="Line {line}: {message}"
        values={{ line: item.line, message: item.message }}
      />
    );

  return (
    // Focusable so the editor can own undo/redo and Delete while the pointer is
    // anywhere inside it, without a document-level listener that would fire for
    // every other drawer on the page.
    <div
      ref={shellRef}
      className={styles.visualEditorBody}
      onKeyDown={handleKeyDown}
      onMouseDown={handleMouseDown}
      tabIndex={-1}
      role="presentation"
    >
      <Space direction="vertical" size={8} className={styles.notices}>
        {/**
         * Why the save failed, above every local notice.
         *
         * The Save button belongs to the host drawer, but the mistake is in the
         * body, so this is where the report has to land — and here rather than
         * beside the button, because it survives scrolling and every view
         * switch. The preflight bars below describe an email that would render
         * badly; this one says nothing was stored at all, which outranks them.
         */}
        {serverErrors.length > 0 && (
          <Alert
            className={styles.noticeBar}
            type="error"
            showIcon
            message={
              <F
                id="pages.emailCenter.visual.saveRejected"
                defaultMessage="{count, plural, one {The email was not saved — one problem in its content.} other {The email was not saved — # problems in its content.}}"
                values={{ count: serverErrors.length }}
              />
            }
            description={
              serverErrors.length === 1 ? (
                serverErrorText(serverErrors[0])
              ) : (
                <ul className={styles.issueList}>
                  {serverErrors.map((item, index) => (
                    <li key={`${item.line ?? 'no-line'}-${index}`}>
                      {serverErrorText(item)}
                    </li>
                  ))}
                </ul>
              )
            }
          />
        )}
        {/*
          No standing notice for a contained (custom-HTML) body: the missing
          Design view and the rail hint already say what it is, and an info bar
          restating it on every open pushed the editor off screen. Only the
          divergence warning — the one with a decision in it — earns a bar.
        */}
        {/* A contained body has no visual version to be out of date, and
            "restore" it would only throw away the source edits just made. */}
        {detached && !structureLocked && !detachAcknowledged && (
          <Alert
            className={styles.noticeBar}
            type="warning"
            showIcon
            message={
              <F
                id="pages.emailCenter.visual.detached"
                defaultMessage="This email was edited as code, so the visual version is out of date."
              />
            }
            description={
              <F
                id="pages.emailCenter.visual.detachedHelp"
                defaultMessage="Your code is what gets saved and sent. You can keep editing it here, or go back to the visual version — which replaces the code."
              />
            }
            action={
              <Space direction="vertical" size={4}>
                {/* Restoring rewrites the body, so it is an edit — absent when the
                    template cannot be edited, where the banner is only an explanation
                    of what is on screen. */}
                {!readOnly && (
                  <Button size="small" onClick={handleRestoreVisual}>
                    <F
                      id="pages.emailCenter.visual.restoreVisual"
                      defaultMessage="Restore the visual version"
                    />
                  </Button>
                )}
                <Button
                  size="small"
                  type="text"
                  onClick={() => setDetachAcknowledged(true)}
                >
                  <F
                    id="pages.emailCenter.visual.keepCode"
                    defaultMessage="Keep editing as code"
                  />
                </Button>
              </Space>
            }
          />
        )}
        {stranded.length > 0 && (
          <Alert
            className={styles.noticeBar}
            type="warning"
            showIcon
            message={
              <F
                id="pages.emailCenter.visual.strandedContent"
                defaultMessage="{count, plural, one {# block sits in a part the current settings do not show, so it would be left out of the email.} other {# blocks sit in parts the current settings do not show, so they would be left out of the email.}}"
                values={{
                  count: stranded.reduce(
                    (total, slot) => total + slot.count,
                    0,
                  ),
                }}
              />
            }
            description={
              <F
                id="pages.emailCenter.visual.strandedContentHelp"
                defaultMessage="Marked in “Structure”. Either move the content to a visible part, or change the block’s layout so that part is shown."
              />
            }
          />
        )}
        {/* Says why the design tools are missing rather than letting a phone user
            hunt for them. Info, not warning: nothing is wrong, this email is simply
            read here and built at a desk. */}
        {isMobile && !readOnly && (
          <Alert
            className={styles.noticeBar}
            type="info"
            showIcon
            message={
              <F
                id="pages.emailCenter.visual.mobileReadOnly"
                defaultMessage="Designing this email needs a bigger screen. Here you can preview it, check its settings and send yourself a test."
              />
            }
          />
        )}
        {noticeAlert(
          'error',
          errors,
          <F
            id="pages.emailCenter.visual.blockingIssues"
            defaultMessage="{count, plural, one {# problem} other {# problems}} would stop this email from rendering correctly."
            values={{ count: errors.length }}
          />,
        )}
        {noticeAlert(
          'warning',
          advisories,
          <F
            id="pages.emailCenter.visual.advisoryIssues"
            defaultMessage="{count, plural, one {# note} other {# notes}} about how this email will render."
            values={{ count: advisories.length }}
          />,
        )}
      </Space>

      <div className={styles.toolbar}>
        <Space size={8}>
          <Segmented
            size="small"
            value={centreView}
            onChange={(next) =>
              setCentreView(
                next as 'design' | 'source' | 'preview' | 'settings',
              )
            }
            options={[
              /**
               * Design is dropped for a structure-locked body.
               *
               * A legacy/custom-HTML template is held whole in one `native.html`
               * block, so the canvas can only show that one opaque block and the
               * palette refuses every drop. Offering the view means offering an
               * editor that cannot edit — Source and Settings are the two that
               * actually do something here.
               */
              ...(structureLocked || isMobile
                ? []
                : [
                    {
                      value: 'design',
                      label: getIntlTranslation(intl, {
                        id: 'pages.emailCenter.visual.view.design',
                        defaultMessage: 'Design',
                      }),
                    },
                  ]),
              // Source is two panes — the Twig buffer and what it renders — and it is
              // the pane pairing that makes it worth having. One at a time on a phone
              // would be a code editor with no feedback, so it is left out.
              ...(isMobile
                ? []
                : [
                    {
                      value: 'source',
                      label: getIntlTranslation(intl, {
                        id: 'pages.emailCenter.visual.view.source',
                        defaultMessage: 'Source',
                      }),
                    },
                  ]),
              {
                value: 'preview',
                label: getIntlTranslation(intl, {
                  id: 'pages.emailCenter.visual.view.preview',
                  defaultMessage: 'Preview',
                }),
              },
              {
                value: 'settings',
                label: getIntlTranslation(intl, {
                  id: 'pages.emailCenter.visual.view.settings',
                  defaultMessage: 'Settings',
                }),
              },
              /**
               * Announced, not yet built — and only for the kind of mail it
               * will ever apply to. A transactional mail is sent by its
               * journey trigger, so an automation view would be a promise we
               * are not going to keep there; a marketing mail is the one sent
               * by an automation step.
               *
               * Disabled rather than hidden: the point is to say the view is
               * coming, which a missing option cannot do.
               */
              ...(isMarketing
                ? [
                    {
                      value: 'automation',
                      disabled: true,
                      label: (
                        <Tooltip
                          title={
                            <F
                              id="pages.emailCenter.visual.view.automationSoon"
                              defaultMessage="Coming soon"
                            />
                          }
                        >
                          <span>
                            {getIntlTranslation(intl, {
                              id: 'pages.emailCenter.visual.view.automation',
                              defaultMessage: 'Automation',
                            })}
                          </span>
                        </Tooltip>
                      ),
                    },
                  ]
                : []),
            ]}
          />
          {/* Device widths matter for both the editing canvas and the preview. */}
          {!inSettings && (
            <Segmented
              size="small"
              value={deviceWidth}
              onChange={(next) => setDeviceWidth(Number(next))}
              options={DEVICE_WIDTHS.map(({ width: px, icon }) => ({
                value: px,
                label: (
                  <Tooltip title={`${px}px`}>
                    <span className={styles.deviceIcon}>{icon}</span>
                    {/* The tooltip names it for a mouse; a Segmented item is a radio
                        whose name comes from its label's text, and an icon has none. */}
                    <span className={styles.srOnly}>{`${px}px`}</span>
                  </Tooltip>
                ),
              }))}
            />
          )}
          {/* Which language the preview renders in — beside the device sizes
              because both answer "how am I looking at this", not "what does it
              say". Small and inline, so it reads as a viewing control rather
              than a property of the template. */}
          {onPreviewLanguageChange && (
            <Select
              size="small"
              variant="borderless"
              className={styles.languageSelect}
              value={activeLanguage}
              onChange={onPreviewLanguageChange}
              options={(languageOptions?.length
                ? languageOptions
                : LANGUAGE_CODES
              ).map((code) => ({ value: code, label: languageLabel(code) }))}
            />
          )}
          {/* Which layout wraps the email. Beside the language because it
              answers the same "how am I looking at this" — but unlike the
              language it persists: the pick is saved as the template's layout.
              Disabled rather than hidden when read-only, so a non-owned
              template still says which layout it uses. Shown on every view,
              including Settings: this is the only place the layout is picked. */}
          {onLayoutChange && !!layoutOptions?.length && (
            <Tooltip
              title={
                <F
                  id="pages.emailCenter.templates.layoutKey"
                  defaultMessage="Layout"
                />
              }
            >
              <Select
                size="small"
                variant="borderless"
                className={styles.layoutSelect}
                value={renderLayoutKey ?? undefined}
                onChange={handleLayoutSwitch}
                options={layoutOptions}
                disabled={readOnly}
                /* The way back to no layout at all — see `handleLayoutSwitch`. */
                allowClear={!readOnly}
                popupMatchSelectWidth={false}
                placeholder={
                  <F
                    id="pages.emailCenter.visual.noLayout"
                    defaultMessage="No layout"
                  />
                }
              />
            </Tooltip>
          )}
        </Space>

        {/* Editing controls are absent in Preview and for a read-only template, not
            disabled: neither can edit, and greyed-out editing tools around a view
            that cannot change are just noise to read past. */}
        <Space size={4}>
          {!previewing && !readOnly && (
            <>
              <Tooltip
                title={
                  <F
                    id="pages.emailCenter.visual.undo"
                    defaultMessage="Undo (⌘Z)"
                  />
                }
              >
                <Button
                  size="small"
                  type="text"
                  icon={<UndoOutlined />}
                  /* An antd Tooltip does not name a button for assistive tech. */
                  aria-label={getIntlTranslation(intl, {
                    id: 'pages.emailCenter.visual.undo',
                    defaultMessage: 'Undo (⌘Z)',
                  })}
                  disabled={!canUndo(state)}
                  onClick={() => dispatch({ type: 'undo' })}
                />
              </Tooltip>
              <Tooltip
                title={
                  <F
                    id="pages.emailCenter.visual.redo"
                    defaultMessage="Redo (⇧⌘Z)"
                  />
                }
              >
                <Button
                  size="small"
                  type="text"
                  icon={<RedoOutlined />}
                  aria-label={getIntlTranslation(intl, {
                    id: 'pages.emailCenter.visual.redo',
                    defaultMessage: 'Redo (⇧⌘Z)',
                  })}
                  disabled={!canRedo(state)}
                  onClick={() => dispatch({ type: 'redo' })}
                />
              </Tooltip>
            </>
          )}
        </Space>
      </div>

      <DndContext
        sensors={sensors}
        onDragStart={handleDragStart}
        onDragMove={handleDragMove}
        onDragEnd={handleDragEnd}
        onDragCancel={endDrag}
      >
        <div className={styles.workspace}>
          {/**
           * The design rails are **gone** in Preview, not faded in place.
           *
           * Owner-stated: *"when I click on preview, the design things should fade and
           * I should only be able to populate fields."* Leaving them mounted but
           * translucent would keep 550 px of the width they occupy — and the point of
           * a preview is to see the email, so the canvas takes that space instead.
           */}
          {!previewing && !inSettings && !inSource && !isMobile && (
            <div className={styles.rail}>
              <section
                className={styles.railSection}
                data-role="blocks"
                data-open={railOpen.blocks ? '1' : undefined}
              >
                <button
                  type="button"
                  className={styles.railSectionHeader}
                  aria-expanded={railOpen.blocks}
                  onClick={() => toggleRailSection('blocks')}
                >
                  <span className={styles.railChevron}>
                    {railOpen.blocks ? <DownOutlined /> : <RightOutlined />}
                  </span>
                  <F
                    id="pages.emailCenter.visual.blocksTitle"
                    defaultMessage="Blocks"
                  />
                </button>
                {railOpen.blocks && (
                  <div className={styles.railSectionBody}>
                    {structureLocked ? (
                      <span className={styles.railHint}>
                        <F
                          id="pages.emailCenter.visual.lockedStructure"
                          defaultMessage="This email is custom HTML. Edit it under “Eigenes HTML” in the properties panel, or in the Source view — blocks cannot be added around it."
                        />
                      </span>
                    ) : (
                      <PaletteRail
                        registry={registry}
                        labels={componentLabels}
                        disabled={arrangingDisabled}
                        onInsert={handleInsert}
                      />
                    )}
                  </div>
                )}
              </section>

              <section
                className={styles.railSection}
                data-role="structure"
                data-open={railOpen.structure ? '1' : undefined}
              >
                <button
                  type="button"
                  className={styles.railSectionHeader}
                  aria-expanded={railOpen.structure}
                  onClick={() => toggleRailSection('structure')}
                >
                  <span className={styles.railChevron}>
                    {railOpen.structure ? <DownOutlined /> : <RightOutlined />}
                  </span>
                  <F
                    id="pages.emailCenter.visual.layersTitle"
                    defaultMessage="Structure"
                  />
                </button>
                {railOpen.structure && (
                  <div className={styles.railSectionBody}>
                    <LayersTree
                      document={document}
                      registry={registry}
                      labels={componentLabels}
                      selectedBlockId={selectedBlockId}
                      selectedSlot={preferredSlot}
                      renderedSlots={renderedSlots}
                      onSelectBlock={handleSelectBlock}
                      onSelectSlot={handleSelectSlot}
                    />
                  </div>
                )}
              </section>
            </div>
          )}

          {inSettings ? (
            <div className={styles.settingsView}>
              <div className={styles.settingsForm}>{settingsSlot}</div>
            </div>
          ) : centreView === 'preview' ? (
            <div className={styles.previewLayout}>
              <CanvasFrame
                html={body}
                document={document}
                previewContext={sample.mergedContext}
                translations={translations}
                fragments={fragments}
                layoutKey={renderLayoutKey}
                width={deviceWidth}
                device={deviceKind}
                layoutIssueReported={layoutIssueReported}
                previewOnly
              />
              {/**
               * The rail is *dropped*, not emptied, when it would hold nothing.
               *
               * The whole variable half is withheld for NL2Go templates: their
               * placeholders are named differently, so these fields would fill in
               * nothing. Their bodies carry no `trans()` keys either, so the rail
               * that remains is an empty 340 px column stealing width from the one
               * thing a preview is for. The email texts still show wherever they
               * exist — wording is shared across providers.
               */}
              {sampleRailShown && (
                <div className={styles.sampleRail}>
                  {!variablesUnsupported && (
                    <>
                      <div className={styles.railTitle}>
                        <F
                          id="pages.emailCenter.visual.preview.dataTitle"
                          defaultMessage="Data for this preview"
                        />
                      </div>
                      <span className={styles.railHint}>
                        <F
                          id="pages.emailCenter.visual.preview.dataHint"
                          defaultMessage="Fill in what an email would be sent with. Nothing here is saved with the template."
                        />
                      </span>

                      {/**
                       * …or take the values off a real lead or appointment, which is
                       * the faster way to see whether the email holds up against the
                       * data it will actually be sent with.
                       */}
                      <SampleSourcePicker
                        language={activeLanguage}
                        onApply={sample.applyValues}
                        onClear={sample.clearValues}
                      />

                      <TestDataPanel
                        appliedSourceLabel={sample.appliedSource?.label}
                        sections={[
                          {
                            title: (
                              <F
                                id="pages.emailCenter.editor.testData"
                                defaultMessage="Sample data"
                              />
                            ),
                            rows: sample.placeholderPaths.map((path) => ({
                              key: path,
                              ...humanizeVariablePath(path),
                              value: sample.values[path] ?? '',
                              placeholder: sample.mergedContext[path],
                              sourceLabel: sample.appliedSource?.label,
                              sourceValue: sample.appliedSource?.values[path],
                            })),
                            onChange: sample.setValue,
                            onRestore: sample.setValue,
                          },
                          {
                            title: (
                              <F
                                id="pages.emailCenter.editor.conditions"
                                defaultMessage="Optional content"
                              />
                            ),
                            hint: (
                              <F
                                id="pages.emailCenter.editor.conditionsHint"
                                defaultMessage="These values decide which parts of the email show. Enter a value to try a part — 'false' or '0' hides it."
                              />
                            ),
                            rows: sample.conditionPaths.map((path) => ({
                              key: path,
                              ...humanizeVariablePath(path),
                              value: sample.values[path] ?? '',
                              placeholder: sample.mergedContext[path],
                              sourceLabel: sample.appliedSource?.label,
                              sourceValue: sample.appliedSource?.values[path],
                            })),
                            onChange: sample.setValue,
                            onRestore: sample.setValue,
                          },
                        ]}
                      />
                    </>
                  )}

                  {
                    /**
                     * The email's texts, as the **real** translation fields.
                     *
                     * These used to be preview-only override boxes: typing in one changed
                     * what this preview showed and nothing else, which is the opposite of
                     * what someone reading their own email in thirteen languages wants. Each
                     * row is now the same control as everywhere else — the resolved wording,
                     * its key, and a click to edit every language — so a fix made while
                     * proof-reading is a fix.
                     *
                     * Wording-only: there is no `onChange`, because this panel lists the keys
                     * the email *uses*. Re-pointing one belongs in the block that uses it.
                     */
                    sample.transKeys.length > 0 && (
                      <div
                        className={
                          variablesUnsupported
                            ? styles.sampleTexts
                            : `${styles.sampleTexts} ${styles.sampleTextsCapped}`
                        }
                      >
                        {/* Collapsible, and collapsed by default, for the same reason
                        the sample data is: a template with a dozen translated
                        strings turns this rail into a wall of textareas that
                        pushes the actual preview data off-screen. The count on
                        the header says how much is hidden. */}
                        <div
                          className={styles.sampleTextsTitle}
                          role="button"
                          tabIndex={0}
                          onClick={() => setTextsCollapsed((v) => !v)}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              setTextsCollapsed((v) => !v);
                            }
                          }}
                        >
                          {textsCollapsed ? (
                            <RightOutlined />
                          ) : (
                            <DownOutlined />
                          )}
                          <F
                            id="pages.emailCenter.preview.translations"
                            defaultMessage="Email texts"
                          />
                          <span className={styles.sampleTextsCount}>
                            {sample.transKeys.length}
                          </span>
                        </div>
                        {!textsCollapsed && (
                          <div className={styles.sampleTextsList}>
                            {sample.transKeys.map((key) => (
                              <div key={key} className={styles.sampleTextRow}>
                                <span className={styles.sampleTextLabel}>
                                  {humanizeTransKey(key).label}
                                </span>
                                <InputWithPartnerTranslation
                                  value={bindingExpression(key)}
                                  language={activeLanguage}
                                />
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )
                  }
                </div>
              )}
            </div>
          ) : centreView === 'design' ? (
            <CanvasFrame
              html={designBody}
              document={document}
              previewContext={sample.mergedContext}
              translations={translations}
              fragments={fragments}
              layoutKey={renderLayoutKey}
              width={deviceWidth}
              device={deviceKind}
              selectedBlockId={selectedBlockId}
              onSelectBlock={handleSelectBlock}
              onMeasure={handleMeasure}
              dragPoint={dragPoint}
              draggingBlockId={
                dragSubject?.kind === 'block' ? dragSubject.blockId : null
              }
              onDropTargetChange={handleDropTargetChange}
              selectedLabel={selectedLabel}
              conditionalBlocks={conditionalBlocks}
              onContextMenu={arrangingDisabled ? undefined : openBlockMenu}
              layoutIssueReported={layoutIssueReported}
            />
          ) : (
            <div className={styles.sourceLayout}>
              <div className={styles.sourcePane}>
                <SourcePanel
                  body={body}
                  onOpenFragment={onOpenFragment}
                  onChange={readOnly ? undefined : handleSourceChange}
                  variableGroups={variableGroups}
                  insertableFragments={insertableFragments}
                  variablesUnsupported={variablesUnsupported}
                />
              </div>
              {/* The same renderer as Design and Preview, chrome off — editing Twig by
                  hand is exactly when you need to see what it renders. */}
              <div className={styles.sourcePreviewPane}>
                <CanvasFrame
                  html={body}
                  document={document}
                  previewContext={previewContext}
                  translations={translations}
                  fragments={fragments}
                  layoutKey={renderLayoutKey}
                  width={deviceWidth}
                  device={deviceKind}
                  layoutIssueReported={layoutIssueReported}
                  previewOnly
                />
              </div>
            </div>
          )}

          {/**
           * The right rail is the **selected block**, and nothing else.
           *
           * It used to be two tabs, Email and Block. The email's own properties — name,
           * type, subject, layout, sender — are template metadata, set once and rarely
           * revisited; the block inspector is used on every click. Making them share a
           * rail meant the thing you reach for constantly was one tab away, behind the
           * thing you almost never touch. The email's properties are now a *view*
           * (`Settings`, beside Design/Source/Preview), where they get the full width a
           * form wants.
           */}
          {!previewing && !inSettings && !inSource && !isMobile && (
            <div className={styles.inspectorRail}>
              <div className={styles.railTitle}>
                <span className={styles.railTitleText}>
                  {selectedLabel ?? (
                    <F
                      id="pages.emailCenter.visual.rail.block"
                      defaultMessage="Block"
                    />
                  )}
                </span>
                {/**
                 * **When this block shows**, as a peer of the other two block
                 * actions rather than a row of its own below them.
                 *
                 * Conditions are rare, so the unused state has to cost nothing —
                 * and an icon among the block's actions costs nothing at all,
                 * where the section it opens would have cost a heading and a row.
                 *
                 * The eye *shows and hides the editor* and never clears what is
                 * stored — it used to toggle the condition itself, so the button
                 * that looks like "let me see this" silently deleted a rule the
                 * author had typed. The count badge is what tells you a condition
                 * exists while the editor is shut.
                 */}
                {selectedBlockId && !readOnly && (
                  <Badge
                    count={conditionCount}
                    size="small"
                    offset={[-4, 4]}
                    className={styles.railActionBadge}
                  >
                    <Tooltip
                      title={
                        conditionsOpen ? (
                          <F
                            id="pages.emailCenter.visual.rail.hideCondition"
                            defaultMessage="Hide this block’s conditions"
                          />
                        ) : conditionCount > 0 ? (
                          <F
                            id="pages.emailCenter.visual.rail.editCondition"
                            defaultMessage="Show this block’s conditions"
                          />
                        ) : (
                          <F
                            id="pages.emailCenter.visual.rail.addCondition"
                            defaultMessage="Show this block only under a condition"
                          />
                        )
                      }
                    >
                      <Button
                        size="small"
                        type="text"
                        // Disabled rather than hidden when the document is detached,
                        // matching how the properties below it behave.
                        disabled={propertiesDisabled}
                        className={
                          // Lit for both states the author cares about: the block
                          // has conditions, or their editor is open.
                          conditionCount > 0 || conditionsOpen
                            ? styles.railActionOn
                            : undefined
                        }
                        icon={
                          conditionCount > 0 ? (
                            <EyeOutlined />
                          ) : (
                            <EyeInvisibleOutlined />
                          )
                        }
                        aria-label={getIntlTranslation(intl, {
                          id: 'pages.emailCenter.visual.rail.addCondition',
                          defaultMessage:
                            'Show this block only under a condition',
                        })}
                        onClick={() => {
                          const open = !conditionsOpen;
                          setConditionsToggle({
                            blockId: selectedBlockId,
                            open,
                          });
                          // Closing drops a condition with no usable clause; anything
                          // that would compile is left exactly as typed.
                          if (
                            !open &&
                            selectedBlock?.visibility &&
                            conditionCount === 0
                          ) {
                            handleChangeVisibility(null);
                          }
                        }}
                      />
                    </Tooltip>
                  </Badge>
                )}
                {/* Acts on the selected block, so it belongs on the block's own
                    heading rather than at the bottom of its properties. */}
                {selectedBlockId && selectedFragmentKey && (
                  <Tooltip
                    title={
                      <F
                        id="pages.emailCenter.visual.rail.editFragment"
                        defaultMessage="Edit this building block"
                      />
                    }
                  >
                    <Button
                      size="small"
                      type="text"
                      icon={<EditOutlined />}
                      onClick={() => onOpenFragment?.(selectedFragmentKey)}
                    />
                  </Tooltip>
                )}
                {selectedBlockId && !arrangingDisabled && (
                  <Tooltip
                    title={
                      <F
                        id="pages.emailCenter.visual.rail.deleteBlock"
                        defaultMessage="Remove this block (⌫)"
                      />
                    }
                  >
                    <Button
                      size="small"
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      aria-label={getIntlTranslation(intl, {
                        id: 'pages.emailCenter.visual.rail.deleteBlock',
                        defaultMessage: 'Remove this block (⌫)',
                      })}
                      onClick={() =>
                        dispatch({
                          type: 'deleteBlock',
                          blockId: selectedBlockId,
                        })
                      }
                    />
                  </Tooltip>
                )}
              </div>
              <InspectorPanel
                block={selectedBlock}
                registry={registry}
                labels={componentLabels}
                variableGroups={variableGroups}
                previewContext={previewContext}
                disabled={propertiesDisabled}
                onChangeProps={handleChangeProps}
                onEndEditRun={handleEndEditRun}
                onOpenFragment={onOpenFragment}
                language={activeLanguage}
                templateName={templateName}
                condition={selectedBlock?.visibility ?? null}
                conditionOpen={conditionsOpen}
                onChangeVisibility={
                  readOnly ? undefined : handleChangeVisibility
                }
              />
            </div>
          )}
        </div>

        {/**
         * What the cursor carries. Without it a drag was invisible: the drop line is
         * 3 px of the *destination*, and nothing said what was in flight — a block
         * move showed nothing at all, because the grip it started from unmounts.
         *
         * `dropAnimation={null}`: the chip is a label, not the block itself, so
         * flying it into place would animate a lie. It disappears where it was
         * released and the canvas re-renders with the block in position.
         */}
        {/* Fixed-positioned at the pointer, so it is not clipped by the canvas's
            own scroll container the way an in-flow menu would be. */}
        {blockMenu && (
          <div
            className={styles.blockMenu}
            style={{ top: blockMenu.y, left: blockMenu.x }}
            role="menu"
          >
            <button
              type="button"
              className={styles.blockMenuItem}
              onClick={() => {
                dispatch({
                  type: 'duplicateBlock',
                  blockId: blockMenu.blockId,
                });
                closeBlockMenu();
              }}
            >
              <CopyOutlined />
              <F
                id="pages.emailCenter.visual.menu.duplicate"
                defaultMessage="Duplicate"
              />
              <span className={styles.blockMenuHint}>⌘D</span>
            </button>
            <button
              type="button"
              className={`${styles.blockMenuItem} ${styles.blockMenuDanger}`}
              onClick={() => {
                dispatch({ type: 'deleteBlock', blockId: blockMenu.blockId });
                closeBlockMenu();
              }}
            >
              <DeleteOutlined />
              <F
                id="pages.emailCenter.visual.menu.delete"
                defaultMessage="Remove"
              />
              <span className={styles.blockMenuHint}>⌫</span>
            </button>
          </div>
        )}

        <DragOverlay dropAnimation={null}>
          {dragLabel && (
            <span className={styles.dragChip}>
              <HolderOutlined />
              {dragLabel}
            </span>
          )}
        </DragOverlay>
      </DndContext>
    </div>
  );
};

export default React.memo(VisualEditorBody);
